<script lang="ts" setup>
  import { useElementSize } from '@vueuse/core';
  import { computed, nextTick, onMounted, onUnmounted, ref, useSlots, watch } from 'vue';
  import { SegmentedProps } from './segmented';

  defineOptions({
    name: 'ISegmented',
  });

  const props = defineProps(SegmentedProps);
  const emits = defineEmits(['update:modelValue', 'change']);

  // ref 引用
  const segmentedControl = ref<HTMLElement | null>(null);
  // 当前选中的索引
  const activeSegment = ref(-1);

  // 获取每个按钮的宽度
  const buttonWidths = ref<number[]>([]);

  const { width: cw } = useElementSize(segmentedControl);

  // 更新按钮宽度
  const updateButtonWidths = () => {
    nextTick(() => {
      setTimeout(() => {
        // 同样增加延迟以确保样式已完全应用
        const buttons = segmentedControl.value?.querySelectorAll('.i-segmented-control__item') || [];
        buttonWidths.value = Array.from(buttons).map(button => {
          return button.getBoundingClientRect().width;
        });
        activeSegment.value = props.segments.findIndex(it => it.value === props.modelValue);
      }, 100); // 延迟 100ms
    });
  };

  // 在组件挂载后获取按钮宽度
  onMounted(() => {
    updateButtonWidths();
    // 监听窗口大小变化，重新计算按钮宽度
    window.addEventListener('resize', updateButtonWidths);
  });

  // 监听 modelValue 的变化
  watch(
    () => props.modelValue,
    newValue => {
      activeSegment.value = props.segments.findIndex(it => it.value === newValue);
    },
  );

  // 在组件卸载时移除 resize 事件监听
  onUnmounted(() => {
    window.removeEventListener('resize', updateButtonWidths);
  });

  // 计算当前激活分段的样式偏移量
  const activeOffset = computed(() => {
    if (buttonWidths.value.length === 0) return '0px';
    const offset = buttonWidths.value.slice(0, activeSegment.value).reduce((sum, width) => sum + width, 0);
    return `${offset}px`;
  });

  // 切换分段的方法
  const selectSegment = (index: number, disabled?: boolean) => {
    if (disabled) return;
    activeSegment.value = index;
    nextTick(() => {
      emits('update:modelValue', props.segments[index].value);
      emits('change', props.segments[index].value);
    });
  };

  const slots = useSlots();
</script>

<template>
  <div ref="segmentedControl" class="i-segmented" :class="[`i-segmented__${size || 'default'}`]">
    <div class="i-segmented-control">
      <!-- 分段按钮 -->
      <div
        v-for="(segment, index) in segments"
        :key="index"
        class="i-segmented-control__item"
        :class="{ active: activeSegment === index, 'i-segmented-control__disabled': segment.disabled }"
        @click="selectSegment(index, segment.disabled)"
      >
        <slot v-if="slots.default" name="default" :segment="segment" :index="index" />
        <span
          v-else
          class="i-segmented-label"
          :style="{ width: block ? `${cw / segments.length - 20}px` : '100%' }"
          :title="segment.label"
          >{{ segment.label }}</span
        >
      </div>

      <!-- 平移动效指示器 -->
      <span
        class="indicator"
        :style="{
          transform: `translateX(${activeOffset})`,
          width: block ? `calc(100% / ${segments.length})` : `${buttonWidths[activeSegment]}px`,
        }"
      />
    </div>
  </div>
</template>
